// Polls the workflow runs list every `intervalValue` ms while any run is non-terminal.
// Stops automatically once all visible runs have data-terminal="true".
// Uses a single <turbo-frame> reload rather than a manual fetch, so Turbo handles
// the DOM update natively — no custom diffing required.

import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["frame", "run"]
  static values = {
    src:      String,
    interval: { type: Number, default: 5000 },
  }

  connect() {
    if (this._hasActiveRuns()) {
      this._startPolling()
    }
  }

  disconnect() {
    this._stopPolling()
  }

  // Called by Turbo after the frame finishes loading (turbo:frame-load event on the frame target).
  // Starts polling on first load if active runs exist; stops once all runs are terminal.
  frameLoaded() {
    if (this._hasActiveRuns()) {
      this._startPolling()
    } else {
      this._stopPolling()
    }
  }

  // ── private ──────────────────────────────────────────────────────────────

  _hasActiveRuns() {
    return this.runTargets.some(row => row.dataset.terminal === "false")
  }

  _startPolling() {
    if (this._timer) return
    this._timer = setInterval(() => this._refresh(), this.intervalValue)
  }

  _stopPolling() {
    if (!this._timer) return
    clearInterval(this._timer)
    this._timer = null
  }

  _refresh() {
    if (!this.hasFrameTarget) return
    // Force a fresh request by appending a timestamp — prevents browser/CDN cache
    // returning stale content when the URL doesn't change between polls.
    try {
      const url = new URL(this.srcValue, window.location.origin)
      url.searchParams.set("_t", Date.now())
      this.frameTarget.src = url.toString()
    } catch (_) {
      this.frameTarget.reload()
    }
  }
}
